import type { Bounds } from "./PlotDataStreamer";

/**
 * A chart's two x-axis coordinate spaces: the visible x-axis window in chart
 * seconds, and the horizontal pixel band the chart area occupies.
 */
export interface ChartXGeometry {
  /** The chart's visible x-axis window, in chart seconds. */
  viewportDomain: Bounds;
  /**
   * The chart area's pixel origin and width. The origin is whatever the caller
   * measures its own pixel positions from: 0 when those are already relative to
   * the chart area's left edge, or the chart area's offset within the canvas
   * when they are relative to the canvas.
   */
  pixel: { left: number; width: number };
}

/**
 * The pixel x position where a domain x value (in chart seconds) is drawn.
 *
 * Inverse of {@link pixelToDomainX}: converting a domain value to a pixel and
 * back returns the original value (within floating-point precision), so a mark
 * drawn at some domain x and a click on that same pixel resolve to the same
 * time.
 *
 * Returns undefined when the viewport or the chart area has zero or negative
 * width, where no pixel corresponds to any time.
 */
export function domainXToPixel(
  domainX: number,
  geometry: ChartXGeometry,
): number | undefined {
  const { viewportDomain, pixel } = geometry;
  const viewportWidth = viewportDomain.max - viewportDomain.min;
  if (viewportWidth <= 0 || pixel.width <= 0) {
    return undefined;
  }
  return (
    pixel.left + ((domainX - viewportDomain.min) / viewportWidth) * pixel.width
  );
}

/**
 * The domain x value (in chart seconds) drawn at a pixel x position.
 *
 * Inverse of {@link domainXToPixel}; see its docstring for the round-trip
 * guarantee.
 *
 * Returns undefined when the viewport or the chart area has zero or negative
 * width, where no time lies under any pixel.
 */
export function pixelToDomainX(
  pixelX: number,
  geometry: ChartXGeometry,
): number | undefined {
  const { viewportDomain, pixel } = geometry;
  const viewportWidth = viewportDomain.max - viewportDomain.min;
  if (viewportWidth <= 0 || pixel.width <= 0) {
    return undefined;
  }
  return (
    viewportDomain.min + ((pixelX - pixel.left) / pixel.width) * viewportWidth
  );
}
